<template>
    <b-modal id="modal-notification-confirm" title="Confirmar envío de notificaciones" size="lg" centered scrollable
        hide-footer @hidden="handleHidden">
        <div class="notification-summary">
            <p class="summary-intro">
                Se enviarán notificaciones por correo electrónico a los siguientes responsables con el resumen de sus
                planes de acción asignados:
            </p>

            <div class="responsibles-list">
                <div v-for="(group, index) in groupedChanges" :key="index" class="responsible-group">
                    <div class="responsible-header">
                        <div class="responsible-info">
                            <div class="avatar-circle">
                                {{ getInitials(getWorkerName(group.responsibleId)) }}
                            </div>
                            <span class="responsible-name">
                                {{ getWorkerName(group.responsibleId) }}
                            </span>
                        </div>
                        <span class="plan-count-badge">
                            {{ group.plans.length }} {{ group.plans.length === 1 ? 'plan' : 'planes' }}
                        </span>
                    </div>

                    <div class="plans-container">
                        <div v-for="(plan, pIndex) in group.plans" :key="pIndex" class="plan-item">
                            <div class="plan-summary-row">
                                <div class="plan-index">{{ pIndex + 1 }}</div>
                                <div class="plan-preview-text">
                                    {{ plan.planActionName }}
                                </div>
                                <button class="btn-toggle-detail" @click="togglePlan(plan.planActionId)">
                                    {{ isExpanded(plan.planActionId) ? 'Ocultar' : 'Ver detalle' }}
                                    <i
                                        :class="isExpanded(plan.planActionId) ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                                </button>
                            </div>

                            <b-collapse :visible="isExpanded(plan.planActionId)">
                                <div class="plan-detail-content">
                                    <div class="plan-name-full">{{ plan.planActionName }}</div>
                                    <div v-if="plan.correctiveAction" class="plan-correction">
                                        <span class="correction-label">Acción:</span>
                                        {{ plan.correctiveAction }}
                                    </div>
                                </div>
                            </b-collapse>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-actions">
                <button class="btn-cancel" @click="$bvModal.hide('modal-notification-confirm')">
                    Cancelar
                </button>
                <button class="btn-confirm" @click="confirmSend" :disabled="sending">
                    <i v-if="sending" class="fa fa-spinner fa-spin"></i>
                    <i v-else class="fa fa-paper-plane"></i>
                    {{ sending ? 'Enviando...' : 'Enviar notificaciones' }}
                </button>
            </div>
        </div>
    </b-modal>
</template>

<script>
export default {
    name: "ActionPlanNotificationModal",
    props: {
        changes: {
            type: Array,
            default: () => []
        },
        workers: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            sending: false,
            expandedPlans: {}
        };
    },
    computed: {
        groupedChanges() {
            const groups = {};
            this.changes.forEach(change => {
                if (!groups[change.responsibleId]) {
                    groups[change.responsibleId] = {
                        responsibleId: change.responsibleId,
                        plans: []
                    };
                }
                // Evitar duplicados del mismo plan para el mismo responsable
                const exists = groups[change.responsibleId].plans.some(p => p.planActionId === change.planActionId);
                if (!exists) {
                    groups[change.responsibleId].plans.push(change);
                }
            });
            return Object.values(groups);
        }
    },
    methods: {
        getWorkerName(id) {
            const worker = this.workers.find(w => w.id === id);
            return worker ? `${worker.names} ${worker.surnames}` : 'Desconocido';
        },
        getInitials(name) {
            return name
                .split(' ')
                .map(n => n[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();
        },
        handleHidden() {
            this.sending = false;
            this.expandedPlans = {};
        },
        async confirmSend() {
            this.sending = true;
            this.$emit('confirm');
            // El padre se encarga de cerrar el modal tras el éxito
        },
        togglePlan(id) {
            this.$set(this.expandedPlans, id, !this.expandedPlans[id]);
        },
        isExpanded(id) {
            return !!this.expandedPlans[id];
        }
    }
};
</script>

<style scoped>
.notification-summary {
    padding: 0;
}

.summary-intro {
    color: #4B5563;
    font-size: 14px;
    margin-bottom: 20px;
}

.responsibles-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 24px;
}

.responsible-group {
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    overflow: hidden;
    background: white;
}

.responsible-header {
    background: #F3F4F6;
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #E5E7EB;
}

.responsible-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.avatar-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: white;
    color: #5bc0de;
    border: 1px solid #E5E7EB;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
}

.responsible-name {
    font-weight: 600;
    color: #1F2937;
    font-size: 14px;
}

.plan-count-badge {
    background: #E5E7EB;
    color: #374151;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 12px;
    font-weight: 600;
}

.plans-container {
    padding: 0;
}

.plan-item {
    border-bottom: 1px solid #F3F4F6;
}

.plan-item:last-child {
    border-bottom: none;
}

.plan-summary-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: white;
}

.plan-index {
    width: 24px;
    height: 24px;
    background: #F3F4F6;
    color: #6B7280;
    font-size: 12px;
    font-weight: 600;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.plan-preview-text {
    flex: 1;
    font-size: 13px;
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
}

.btn-toggle-detail {
    border: none;
    background: transparent;
    color: #5bc0de;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s;
}

.btn-toggle-detail:hover {
    background: #eaf8fc;
}

.plan-detail-content {
    padding: 0 16px 16px 52px;
    /* Indent to align with text */
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.plan-name-full {
    font-size: 14px;
    color: #111827;
    line-height: 1.5;
    font-weight: 500;
}

.plan-correction {
    font-size: 13px;
    color: #4B5563;
    line-height: 1.5;
    background: #F9FAFB;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid #F3F4F6;
}

.correction-label {
    font-weight: 600;
    color: #374151;
    margin-right: 4px;
}

/* Modal actions styles */
.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid #E5E7EB;
    margin-top: 16px;
}

.btn-cancel {
    padding: 8px 16px;
    border: 1px solid #D1D5DB;
    background: white;
    color: #374151;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel:hover {
    background: #F3F4F6;
}

.btn-confirm {
    padding: 8px 16px;
    border: none;
    background: #5bc0de;
    color: white;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
}

.btn-confirm:hover {
    background: #31b0d5;
}

.btn-confirm:disabled {
    background: #9CA3AF;
    cursor: not-allowed;
}

.responsibles-list::-webkit-scrollbar {
    display: none;
}
</style>
